/**
 * Filtering, sorting and date display for the Management Portal tables.
 *
 * Dates come from the documents as written:
 *   vendors/{id}.createdAt  — set when the admin registers the vendor
 *   users/{id}.createdAt    — the patient's FIRST scan (desktop scanSync)
 *   users/{id}.lastScanAt   — their most recent scan
 *
 * Documents written before those fields existed have none, so every date here
 * may be missing: it displays as "—", sorts last, and is excluded by any date
 * range rather than guessed into one.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export const DATE_RANGES = [
  { id: 'all', label: 'Any date' },
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
  { id: 'year', label: 'This year' },
  { id: 'custom', label: 'Custom range…' },
];

export const VENDOR_SORTS = [
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'name', label: 'Name A–Z' },
  { id: 'scans', label: 'Most scans' },
];

export const USER_SORTS = [
  { id: 'recent', label: 'Recently scanned' },
  { id: 'newest', label: 'Newest patients' },
  { id: 'oldest', label: 'Oldest patients' },
  { id: 'name', label: 'Name A–Z' },
  { id: 'scans', label: 'Most scans' },
];

/** Milliseconds since epoch for an ISO string, Firestore Timestamp or Date; null if absent. */
export function toTime(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.getTime();
  if (typeof value.toDate === 'function') return toTime(value.toDate());
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/** "26 Sep 2026", or "—" when the document has no date. */
export function formatDate(value) {
  const t = toTime(value);
  if (t === null) return '—';
  return new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "26 Sep 2026, 14:05" for tooltips. */
export function formatDateTime(value) {
  const t = toTime(value);
  if (t === null) return 'Date not recorded';
  return new Date(t).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const startOfDay = (t) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };

/**
 * Whether `value` falls in the range. `from`/`to` are "YYYY-MM-DD" strings from
 * date inputs, used only for the custom range; both ends are inclusive days.
 */
export function inDateRange(value, range, { from = '', to = '', now = Date.now() } = {}) {
  if (!range || range === 'all') return true;
  const t = toTime(value);
  if (t === null) return false;
  switch (range) {
    case 'today': return t >= startOfDay(now);
    case '7d': return t >= now - 7 * DAY_MS;
    case '30d': return t >= now - 30 * DAY_MS;
    case '90d': return t >= now - 90 * DAY_MS;
    case 'year': return new Date(t).getFullYear() === new Date(now).getFullYear();
    case 'custom': {
      // Local-time day bounds, so a patient scanned late on the "to" day is in.
      const lo = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
      const hi = to ? new Date(`${to}T00:00:00`).getTime() + DAY_MS - 1 : Infinity;
      return t >= lo && t <= hi;
    }
    default: return true;
  }
}

const text = (v) => (v ?? '').toString().toLowerCase();
const matches = (needle, ...fields) => !needle || fields.some(f => text(f).includes(needle));

/** Newest first, with undated documents last whichever way the list is sorted. */
function byDate(field, direction) {
  return (a, b) => {
    const ta = toTime(a[field]);
    const tb = toTime(b[field]);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return direction === 'desc' ? tb - ta : ta - tb;
  };
}

const byName = (key) => (a, b) => text(key(a)).localeCompare(text(key(b)));

/**
 * @param vendors  vendor documents
 * @param f        { search, status, range, from, to, sort }
 * @param scansOf  vendor → its scan count (the table's own figure)
 */
export function filterVendors(vendors, f = {}, scansOf = (v) => v.totalScans || 0) {
  const needle = text(f.search).trim();
  const rows = vendors.filter(v =>
    matches(needle, v.name, v.companyName, v.email, v.phone)
    && (!f.status || f.status === 'all' || text(v.status || 'active') === text(f.status))
    && inDateRange(v.createdAt, f.range, f),
  );
  const sort = f.sort || 'newest';
  if (sort === 'name') return rows.sort(byName(v => v.companyName || v.name));
  if (sort === 'scans') return rows.sort((a, b) => scansOf(b) - scansOf(a));
  return rows.sort(byDate('createdAt', sort === 'oldest' ? 'asc' : 'desc'));
}

/** The statuses present in the data, so the filter never offers an empty choice. */
export function vendorStatuses(vendors) {
  return [...new Set(vendors.map(v => text(v.status || 'active')))].sort();
}

/**
 * @param users  user (patient) documents
 * @param f      { search, vendorEmail, arch, foot, dateField, range, from, to, sort }
 *               dateField is 'createdAt' (first scanned) or 'lastScanAt'.
 */
export function filterUsers(users, f = {}) {
  const needle = text(f.search).trim();
  const dateField = f.dateField === 'lastScanAt' ? 'lastScanAt' : 'createdAt';
  const rows = users.filter(u =>
    matches(needle, u.name, u.email, u.phone, u.vendorEmail)
    && (!f.vendorEmail || f.vendorEmail === 'all' || text(u.vendorEmail) === text(f.vendorEmail))
    && (!f.arch || f.arch === 'All' || text(u.medialArchType) === text(f.arch))
    && (!f.foot || f.foot === 'All' || text(u.footType) === text(f.foot))
    && inDateRange(u[dateField], f.range, f),
  );
  const sort = f.sort || 'recent';
  if (sort === 'name') return rows.sort(byName(u => u.name));
  if (sort === 'scans') return rows.sort((a, b) => (b.totalScans || 0) - (a.totalScans || 0));
  if (sort === 'recent') return rows.sort(byDate('lastScanAt', 'desc'));
  return rows.sort(byDate('createdAt', sort === 'oldest' ? 'asc' : 'desc'));
}
